import { ChevronDown } from 'lucide-react';
import { useMemo } from 'react';

import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Skeleton,
  Text,
} from '@/design-system/components/ui';

import { type PayoutRate, type TierPayoutTerm } from '../domain/types';
import { usePayoutTermsByTier } from '../hooks/usePayoutTermsByTier';

interface TierIncentivesCollapsibleProps {
  projectId: string;
  tierId: string;
}

function getPayoutTypeTitle(payoutType: TierPayoutTerm['payout_type']): string {
  switch (payoutType) {
    case 'fixed':
      return 'Fixed Payout';
    case 'variable':
      return 'Variable Payout';
    default:
      return 'Payout';
  }
}

function getPayeeTypeLabel(payeeType: TierPayoutTerm['payee_type']): string {
  switch (payeeType) {
    case 'affiliate':
      return 'Only Referrers';
    case 'end-user':
      return 'Only End Users';
    case 'both':
      return 'Referrers & End Users';
    default:
      return '';
  }
}

function getReferrerLabel(level: number, isMultiLevel: boolean): string {
  if (!isMultiLevel) return 'To referrers';
  if (level === 1) return 'To referrers (Direct Referrals)';
  return `To referrers (Level ${level} Referrals)`;
}

function RateDisplay({ label, rate }: { label: string; rate: PayoutRate }) {
  if (!rate.amount) return null;
  return (
    <div className="tw:flex tw:items-center tw:justify-between tw:py-1.5">
      <Text variant="body-sm" color="muted">
        {label}
      </Text>
      <Text variant="body-sm" weight="medium">
        {rate.amount} {rate.currency ? `${rate.currency} ` : ''}
        {rate.unit}
      </Text>
    </div>
  );
}

function TermCollapsible({ term }: { term: TierPayoutTerm }) {
  const showAffiliateRates = term.payee_type !== 'end-user';
  const showEndUserRate = term.payee_type !== 'affiliate';

  const hasLevel2 = !!term.affiliate2_rate?.amount;
  const hasLevel3 = !!term.affiliate3_rate?.amount;
  const hasLevel4 = !!term.affiliate4_rate?.amount;
  const isMultiLevel = hasLevel2 || hasLevel3 || hasLevel4;

  return (
    <Collapsible className="tw:rounded-lg tw:border tw:border-border tw:bg-background">
      <CollapsibleTrigger className="tw:group tw:flex tw:items-center tw:justify-between tw:w-full tw:px-3 tw:py-2 tw:text-left tw:gap-2">
        <div className="tw:flex tw:flex-col tw:gap-0.5 tw:min-w-0">
          <Text variant="body-sm" weight="medium">
            {getPayoutTypeTitle(term.payout_type)}
          </Text>
          <Text variant="caption" color="muted">
            {term.conversion_name}
          </Text>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          <Badge variant="default" size="sm">
            {getPayeeTypeLabel(term.payee_type)}
          </Badge>
          <ChevronDown
            size={14}
            className="tw:text-muted-foreground tw:transition-transform tw:duration-200 tw:group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="tw:px-3 tw:pb-3 tw:pt-1 tw:border-t tw:border-border">
          <div className="tw:py-1">
            {showAffiliateRates && (
              <>
                <RateDisplay label={getReferrerLabel(1, isMultiLevel)} rate={term.affiliate_rate} />
                {hasLevel2 && <RateDisplay label={getReferrerLabel(2, isMultiLevel)} rate={term.affiliate2_rate} />}
                {hasLevel3 && <RateDisplay label={getReferrerLabel(3, isMultiLevel)} rate={term.affiliate3_rate} />}
                {hasLevel4 && <RateDisplay label={getReferrerLabel(4, isMultiLevel)} rate={term.affiliate4_rate} />}
              </>
            )}
            {showEndUserRate && <RateDisplay label="To end users" rate={term.end_user_rate} />}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function TierIncentivesCollapsible({ projectId, tierId }: TierIncentivesCollapsibleProps) {
  const tierIds = useMemo(() => [tierId], [tierId]);
  const { tierPayoutTerms, isLoading } = usePayoutTermsByTier({ projectId, tierIds });

  if (isLoading) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <Text variant="body-sm" weight="semibold">
          Commission Rates
        </Text>
        <Skeleton variant="rectangular" height={40} />
        <Skeleton variant="rectangular" height={40} />
      </div>
    );
  }

  const tier = tierPayoutTerms.find((t) => t.tier_id === tierId);
  const terms = tier?.payout_terms ?? [];

  if (terms.length === 0) {
    return null;
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <Text variant="body-sm" weight="semibold">
        Commission Rates
      </Text>
      <div className="tw:flex tw:flex-col tw:gap-2">
        {terms.map((term) => (
          <TermCollapsible key={term.conversion_name} term={term} />
        ))}
      </div>
    </div>
  );
}
